import PropTypes from 'prop-types';
import { useMemo, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import useGoalEventProps from '@hooks/useGoalEventProps';
import styled from '@emotion/styled';
import Dropdown from '@comp/Dropdown/Dropdown';
import InfiniteList from '@comp/InfiniteList/InfiniteList';
import EmptyState from '@comp/EmptyState/EmptyState';
import H4 from '@base/H4/H4';
import { BROWSE_FILTER_APPLIED, TOPIC_SYLLABUS_PAGE } from '@constants/segment';
import { segment } from '@actions/analytics';
import { fetchMoreCourses } from '@actions/courses';
import { useDispatchToStore } from '@hooks/reduxHooks';
import useFirstRenderSkip from '@hooks/useFirstRenderSkip';
import { toTitleCase } from '@utils/textHelper';
import useEnroll from '@hooks/useEnroll';
import Dialog from '@comp/Dialog/Dialog';
import { evtNameCategoryMap, staticStrings } from '@constants/index';
import { mediaQuery, Spacings } from '@styles/index';
import {
  getCurrentMoreCoursesFeed,
  getMoreCoursesFeed,
  getMoreCoursesFilters
} from '@selectors/syllabus';
import { getMeUid } from '@selectors/auth';

import {
  getIsSubscribedToPlus,
  getIsSubscribedToOffline
} from '@selectors/subscription';
import useIsRecordedGoal from '@hooks/useIsRecordedGoal';
import { enrollProps, getNoFiltersKey } from './syllabusHelper';
import PlusCard from './PlusCard';

const Title = styled(H4)`
  color: var(--color-text-primary);
  margin-bottom: ${Spacings.SPACING_2B};
  padding: 0;
  grid-column: span 18;
  ${mediaQuery.uptoMobile} {
    margin-bottom: 0;
    grid-column: span 10;
  }
`;

const StyledPlusCard = styled(PlusCard)`
  margin-bottom: ${Spacings.SPACING_2B};
  ${mediaQuery.uptoMobile} {
    grid-column: span 10;
  }
`;

const FiltersContainer = styled.div`
  grid-column: span ${({ gridNumber }) => gridNumber};
  display: grid;
  grid-template-columns: repeat(${({ gridNumber }) => gridNumber}, 1fr);
  grid-column-gap: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_2B};
  ${mediaQuery.uptoMobile} {
    margin: 0px -${Spacings.SPACING_4B};
    grid-column: span 10 / auto;
    display: flex;
    overflow: auto;
    overscroll-behavior: unset;
    scroll-behavior: smooth;
    ::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
    padding: ${Spacings.SPACING_4B} 0px ${Spacings.SPACING_6B}
      ${Spacings.SPACING_4B};
    position: sticky;
    top: ${Spacings.SPACING_12B};
    z-index: 100;
    background: var(--color-base-1);
    & > button {
      box-shadow: none;
      white-space: nowrap;
    }
  }
`;

const createOption = (label, value = '') => {
  return {
    label,
    value
  };
};

const MoreCourses = ({
  topic,
  isContentAvailable,
  goalUID,
  gridNumber,
  lastPrimarySource,
  lastPrimarySourceSection
}) => {
  const isRecordedGoal = useIsRecordedGoal(goalUID);
  const { isUnEnrollModalOpen, toggleEnroll, acceptOnClick, onCloseModal } =
    useEnroll({ ...enrollProps, isRecordedGoal });
  const noFiltersKey = getNoFiltersKey(topic.uid);
  const prevStateKey = useRef(null);
  const filters = useSelector((state) => getMoreCoursesFilters(state, goalUID));
  const dispatch = useDispatchToStore();
  const goalSegmentData = useGoalEventProps({ goalUID });
  const renderBlock = (courseData, index) => {
    return (
      <StyledPlusCard
        key={courseData.course}
        courseData={courseData}
        toggleEnroll={toggleEnroll}
        index={index}
        orientation="vertical"
        gridColumn="span 6"
        lastPrimarySource={lastPrimarySource}
        lastPrimarySourceSection={lastPrimarySourceSection}
      />
    );
  };
  const statusDropdownOptions = useMemo(() => {
    let options = [];
    options.push(createOption('All', ''));
    options = options.concat(
      Object.keys(filters?.course_status || {}).map((key) =>
        createOption(toTitleCase(filters?.course_status[key]), key)
      )
    );
    return options;
  }, [filters?.course_status]);
  const typeDropdownOptions = useMemo(() => {
    let options = [];
    options.push(createOption('All courses', ''));
    options = options.concat(
      Object.keys(filters?.course_type || {}).map((key) =>
        createOption(filters?.course_type[key], key)
      )
    );
    return options;
  }, [filters?.course_type]);
  const languageDropdownOptions = useMemo(() => {
    let options = [];
    options.push(createOption('All languages', ''));
    options = options.concat(
      filters?.languages?.map?.((language) =>
        createOption(language.display_name, language.code)
      )
    );
    return options;
  }, [filters?.languages]);
  const [selectedFilters, setSelectedFilters] = useState({
    status: statusDropdownOptions[0],
    type: typeDropdownOptions[0],
    language: languageDropdownOptions[0]
  });
  const isSubscribed = useSelector(getIsSubscribedToPlus(goalUID));
  const isOfflineSubscription = useSelector(getIsSubscribedToOffline(goalUID));

  const meUid = useSelector(getMeUid);
  const [filterParams, stateKey] = useMemo(() => {
    return Object.keys(selectedFilters || {}).reduce(
      (acc, key) => {
        let params = acc[0];
        let storeKey = acc[1];
        storeKey += `-${key}-${selectedFilters[key]?.value || 'all'}`;
        if (selectedFilters[key]?.value) {
          params = `${params}&${key}=${selectedFilters[key]?.value}`;
        }
        return [params, storeKey];
      },
      ['', topic.uid]
    );
  }, [selectedFilters, topic.uid]);
  const moreCoursesAllFeed = useSelector((state) =>
    getMoreCoursesFeed(state, noFiltersKey)
  );

  const areFiltersChangedRef = useRef(false);
  const feedBlock = useSelector((state) =>
    getCurrentMoreCoursesFeed(
      state,
      stateKey,
      prevStateKey.current,
      areFiltersChangedRef.current
    )
  );

  const fetchData = ({
    areFiltersApplied = false,
    resetWithoutEmpty = false,
    nextPage,
    params = ''
  }) => {
    const promise = dispatch(
      fetchMoreCourses({
        topicUid: topic.uid,
        key: stateKey,
        resetWithoutEmpty,
        nextPage,
        filterParams: `${filterParams}${params}`,
        areFiltersApplied
      })
    );
    if (promise instanceof Promise) {
      promise.then(() => {
        areFiltersChangedRef.current = false;
      });
    } else {
      areFiltersChangedRef.current = false;
    }
    return promise;
  };
  // refresh data after login
  useFirstRenderSkip(() => {
    if (isSubscribed || isOfflineSubscription) {
      fetchData({
        resetWithoutEmpty: true,
        nextPage: true,
        params: `&timestamp=${Date.now()}`
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meUid]);
  const loadMore = (areFiltersApplied = false) => {
    return fetchData({
      nextPage: !areFiltersApplied,
      areFiltersApplied
    });
  };
  const onOptionClick = (type) => (option) => {
    areFiltersChangedRef.current = prevStateKey.current !== stateKey;
    prevStateKey.current = stateKey;
    setSelectedFilters((state) => {
      return {
        ...state,
        [type]: option
      };
    });
    if (lastPrimarySource && lastPrimarySourceSection)
      segment.track(
        BROWSE_FILTER_APPLIED,
        {
          ...goalSegmentData,
          last_primary_source: lastPrimarySource,
          last_primary_source_section: lastPrimarySourceSection
        },
        evtNameCategoryMap.WITHOUT_CATEGORY,
        true
      );
  };

  useFirstRenderSkip(() => {
    loadMore(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters]);

  const content = (
    <>
      {isContentAvailable && <Title as="h2">More Courses</Title>}
      <FiltersContainer gridNumber={gridNumber}>
        <Dropdown
          options={statusDropdownOptions}
          selectedOption={selectedFilters.status}
          onOptionClick={onOptionClick('status')}
          menuWidth="176px"
        />
        <Dropdown
          options={typeDropdownOptions}
          selectedOption={selectedFilters.type}
          onOptionClick={onOptionClick('type')}
          menuWidth="176px"
        />
        <Dropdown
          options={languageDropdownOptions}
          selectedOption={selectedFilters.language}
          onOptionClick={onOptionClick('language')}
          menuWidth="176px"
        />
      </FiltersContainer>
      {!feedBlock.results.length ? (
        !feedBlock.isFetching && (
          <EmptyState
            title="No courses found"
            description="More courses will be added soon. Check back later or try changing the filters."
            gridColumn={
              lastPrimarySource === TOPIC_SYLLABUS_PAGE ? 'span 24' : '6/13'
            }
          />
        )
      ) : (
        <InfiniteList
          data={feedBlock}
          loadMore={loadMore}
          _renderItem={renderBlock}
          gridColumn="span 18"
        />
      )}
      <Dialog
        onClose={onCloseModal}
        dialogState={isUnEnrollModalOpen}
        heading="Are you sure you want to unenroll?"
        message={staticStrings.UNENROLL_MESSAGE}
        acceptLabel="Unenroll"
        rejectLabel="Cancel"
        acceptOnClick={acceptOnClick}
        rejectOnClick={onCloseModal}
      />
    </>
  );

  return moreCoursesAllFeed.results.length > 0 || moreCoursesAllFeed.isFetching
    ? content
    : null;
};

MoreCourses.defaultProps = {
  gridNumber: 18,
  lastPrimarySource: '',
  lastPrimarySourceSection: ''
};

MoreCourses.propTypes = {
  topic: PropTypes.objectOf(PropTypes.string).isRequired,
  isContentAvailable: PropTypes.bool.isRequired,
  goalUID: PropTypes.string.isRequired,
  gridNumber: PropTypes.number,
  lastPrimarySource: PropTypes.string,
  lastPrimarySourceSection: PropTypes.string
};

export default MoreCourses;
